import { CloudFile } from "./client";

export interface SessionSurvey {
    id: string;
    name: string;
    score: number;
    status: SessionSurveyStatus;
    completedAt: Date | null;
}

export interface SessionTimeline {
    timelineId: string;
    timelineName: string;
    surveys: SessionSurvey[];
}

export interface SessionDetailsDto {
    sessionId: string;
    stage: string;
    status: string;
    currentStep: string;
    assessorId: string;
    clientId: string;
    processStartTime: Date | null;
    sessionStartTime: Date | null;
    timeline: string;
    consentAgreement: boolean;
    k10Score: number | null;
    group: string | null;
    subgroup: string | null;

    assessmentOfSuicideSurveyId: string;
    mustCompleteAssessmentOfSuicideDuringBaseline: boolean,
    mustCompleteATSIntervention: boolean,
    isAssessmentOfSuicideCompleted: boolean,
    isAssessmentOfSuicideFormCompleted: boolean,
    surveys: string[];
    processId: string;
    timelineId: string;
    surveyName: string;
    surveyId: string;
    isImpairmentCompleted: boolean;
    isImpairmentFormCompleted: boolean;
    isImpairmentExcluded: boolean;
    timelineStartTime: Date | null;
    assessmentType: FollowUpType;
    startedByAssessor: boolean;
    clientBreaks: SessionBreak[];
    languageId: string;
    impairmentSurveyId: string;
    impairmentCanBeStarted: boolean;
    isTriageCompleted: boolean;
    isTriageConsentCompleted: boolean;
    baselineResultsConfirmed: boolean;
    secondICStatus?: string;
    triageStatus?: string;

    // end notification changes
    tokenStateInfo: SessionTokenStateInfo,
    institutionalData: SessionInstitutionalData,
    voucherData: SessionVoucherData
}

export interface SessionTokenStateInfo {
    isExpired: boolean;
    timelineCompleted: boolean;
    timelineAssessmentType?: FollowUpType;
    timelineName?: string;
}

export interface SessionInstitutionalData {
    study?: string,
    uszCoordinatorPhoneNumber?: string,
    uszCoordinatorEmail?: string
}

export interface SessionVoucherData {
    downloadLink?: string,
    authorizationCode?: string,
    valueInCHF: number
}

export interface SessionBreak {
    id: string;
    timelineId: string;
    surveyId: string;
    duration: number;
}

export interface BreakDuration {
    breakId: string;
    startTime: Date;
    endTime: Date;
    duration: number;
}

export interface ClientSessionInfo {
    id: string;
    title: string;
    status: SessionStatus;
    process: string;
    startTime: Date;
    clientId: string;
    assessorId: string;
}

export interface TimelineEvent {
    eventId: string;
    eventTitle: string;
    eventStartTime: Date;
    status: string;
}

export interface SessionStart {
    hasStarted: boolean;
}

export interface TimelineEventDetails {
    timelineId: string;
    timelineName: string;
    events: TimelineEvent[];
}

export interface SessionFile extends CloudFile {
    order: number;
    documentOriginal?: string;
}

export interface ClientSession {
    id: string;
    startTime: Date;
    active: boolean;
}

export interface SessionAttempt {
    numberOfAttempts: number;
    attemptLimit: number;
}

export interface Assessor {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface SessionEmergencyRequest {
    emergencyHold: boolean;
    reason?: string;
    timeframe?: string;
    timeline?: string;
}

export interface SessionDeactivateRequest {
    reason?: string;
    timeframe: string;
    timeline: string;
}

export interface SessionDialogData {
    action: SessionAction;
    sessionId: string;
    sessionData: SessionEmergencyRequest | SessionDeactivateRequest;
}

export interface DocumentOriginalityDto {
    status: string;
}

export enum SessionAction {
    EMERGENCY = 'EMERGENCY',
    DEACTIVATE = 'DEACTIVATE',
    RESUME = 'RESUME',
}

export enum FollowUpType {
    ONSITE = 'ONSITE',
    ONLINE = 'ONLINE',
    ASSISTED = "ASSISTED",
}

export enum SessionSurveyStatus {
    NOTSTARTED = "NOT_STARTED",
    INPROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED"
}

export enum SessionStatus {
    CANCELLED = "CANCELLED",
    COMPLETED = "COMPLETED",
    ABORT = "ABORT",
    EXCLUDED = "EXCLUDED",
    IN_PROGRESS = "IN_PROGRESS",
    ACTIVE = "ACTIVE",
    BLONLY = "BL_ONLY",
    EXCLUDED_SUICIDALITY = "EXCLUDED_SUICIDALITY",
    AWAITING_EVENT_CREATION = "AWAITING_EVENT_CREATION",
    AWAITING_BASELINE_EVENT_CREATION = "AWAITING_BASELINE_EVENT_CREATION",
    CLIENT_NO_SHOW_UP = "CLIENT_NO_SHOW_UP",
    EMERGENCY_HOLD = "EMERGENCY_HOLD",
    DEACTIVATED = "DEACTIVATED",
    AWAITING_EVENT_CONFIRMATION = "AWAITING_EVENT_CONFIRMATION",
    IMPAIRMENT_EXCLUDED = "IMPAIRMENT_EXCLUDED",
    CONSENT1_NOT_GIVEN = 'CONSENT1_NOT_GIVEN',
    CONSENT2_NOT_GIVEN = 'CONSENT2_NOT_GIVEN',
    TRIAGE = 'TRIAGE'
}

export const SessionStatusText: Record<SessionStatus, string> = {
    [SessionStatus.AWAITING_EVENT_CREATION]: "Timeline completed",
    [SessionStatus.AWAITING_BASELINE_EVENT_CREATION]: "Awaiting baseline event creation",
    [SessionStatus.COMPLETED]: "Completed",
    [SessionStatus.CANCELLED]: "Canceled",
    [SessionStatus.ABORT]: "Aborted",
    [SessionStatus.EXCLUDED]: "Excluded",
    [SessionStatus.IN_PROGRESS]: "In Progress",
    [SessionStatus.EXCLUDED_SUICIDALITY]: "Excluded suicidality",
    [SessionStatus.ACTIVE]: "Active",
    [SessionStatus.BLONLY]: "Baseline Only",
    [SessionStatus.CLIENT_NO_SHOW_UP]: "Didn't attend",
    [SessionStatus.EMERGENCY_HOLD]: "Session on hold",
    [SessionStatus.DEACTIVATED]: "Deactivated",
    [SessionStatus.AWAITING_EVENT_CONFIRMATION]: "Awaiting event confirmation",
    [SessionStatus.IMPAIRMENT_EXCLUDED]: "Excluded impairment",
    [SessionStatus.CONSENT1_NOT_GIVEN]: "Consent 1 not given",
    [SessionStatus.CONSENT2_NOT_GIVEN]: "Consent 2 not given",
    [SessionStatus.TRIAGE]: "Triage",
};

export const SurveyStatusText: Record<SessionSurveyStatus, string> = {
    [SessionSurveyStatus.NOTSTARTED]: "Not started",
    [SessionSurveyStatus.INPROGRESS]: "Ongoing",
    [SessionSurveyStatus.COMPLETED]: "Completed",
};

export const sessionStatusValues = [
    { value: "COMPLETED", name: "COMPLETED" },
    { value: "ABORT", name: "SESSION_ABORT" },
    { value: "ACTIVE", name: "ACTIVE" },
    { value: "BELOW_CUT_OFF", name: "SESSION_BL_ONLY" },
    { value: "EXCLUDED_SUICIDALITY", name: "SESSION_EXCLUDED_SUICIDALITY" },
    { value: "AWAITING_BASELINE_EVENT_CREATION", name: "SESSION_AWAITING_BASELINE_EVENT_CREATION" },
    { value: "CLIENT_NO_SHOW_UP", name: "CLIENT_NO_SHOW_UP" },
    { value: "EMERGENCY_HOLD", name: "SESSION_EMERGENCY_HOLD" },
    { value: "DEACTIVATED", name: "DEACTIVATED" },
    { value: "AWAITING_EVENT_CONFIRMATION", name: "SESSION_AWAITING_EVENT_CONFIRMATION" },
    { value: "IMPAIRMENT_EXCLUDED", name: "SESSION_IMPAIRMENT_EXCLUDED" },
    { value: "CONSENT1_NOT_GIVEN", name: "SESSION_CONSENT1_NOT_GIVEN" },
    { value: "CONSENT2_NOT_GIVEN", name: "SESSION_CONSENT2_NOT_GIVEN" },
    { value: "TRIAGE", name: "TRIAGE_CONSENT" }
];